import axiosInstance, { extractErrorMessage } from './axiosConfig';
import { DataSourceCreate, DataSourceUpdate, QueryResponse, PaginatedDataSourceResponse,
         DataSourceResponse, DataSourceWithAgentsResponse, RegexTestConfiguration,
         RegexTestResponse, BaseResponse } from '../types/dataSource';

// List all data sources with pagination and search
export const listDataSourcesWithPagination = async (
  page: number = 1,
  limit: number = 100,
  search?: string,
  signal?: AbortSignal,
  workspaceId?: number // Optional: omit to get DSs from ALL accessible workspaces (TD users)
): Promise<PaginatedDataSourceResponse> => {
  try {
    const skip = (page - 1) * limit;
    const params: Record<string, unknown> = {
      skip,
      limit
    };

    // Only add workspace_id if provided (undefined = fetch from all accessible workspaces)
    if (workspaceId !== undefined) {
      params.workspace_id = workspaceId;
    }

    if (search) {
      params.search = search;
    }

    const response = await axiosInstance.get('/data-sources', { params, signal });
    return response.data;
  } catch (err: unknown) {
    // Don't throw error if request was aborted (user triggered new search or React StrictMode)
    if (err instanceof Error && (err.name === 'AbortError' || err.name === 'CanceledError')) {
      throw err; // Re-throw original error to preserve error name
    }
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar fontes de dados'
      )
    );
  }
};

// List all data sources (without pagination)
export const listDataSources = async (
  workspaceId?: number // Optional: omit to get DSs from ALL accessible workspaces (TD users)
): Promise<PaginatedDataSourceResponse> => {
  try {
    const params: Record<string, unknown> = { skip: 0, limit: 1000 };

    // Only add workspace_id if provided (undefined = fetch from all accessible workspaces)
    if (workspaceId !== undefined) {
      params.workspace_id = workspaceId;
    }

    const response = await axiosInstance.get('/data-sources', { params });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar fontes de dados'
      )
    );
  }
};

// Get single data source details
export const getDataSource = async (id: number): Promise<DataSourceWithAgentsResponse> => {
  try {
    const response = await axiosInstance.get(`/data-sources/${id}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar fonte de dados'
      )
    );
  }
};

// Create new data source
export const createDataSource = async (data: DataSourceCreate): Promise<DataSourceResponse> => {
  try {
    const response = await axiosInstance.post('/data-sources', data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar fonte de dados'
      )
    );
  }
};

// Update data source
export const updateDataSource = async (id: number, data: DataSourceUpdate): Promise<DataSourceResponse> => {
  try {
    const response = await axiosInstance.put(`/data-sources/${id}`, data);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar fonte de dados'
      )
    );
  }
};

// Delete data source
export const deleteDataSource = async (id: number): Promise<BaseResponse> => {
  try {
    const response = await axiosInstance.delete(`/data-sources/${id}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao excluir fonte de dados'
      )
    );
  }
};

// Execute query for a data source
export const executeQuery = async (id: number, limit: number = 10, offset: number = 0): Promise<QueryResponse> => {
  try {
    const response = await axiosInstance.post(`/data-sources/${id}/execute`, null, {
      params: { limit, offset }
    });
    
    if (response.data?.success && response.data?.data && typeof response.data.data === 'object') {
      const { total, results, limit: resLimit, offset: resOffset } = response.data.data;
      if (Array.isArray(results)) {
        return {
          total,
          results,
          limit: resLimit,
          offset: resOffset
        };
      }
    }
    
    throw new Error('Formato de resposta inválido do servidor');
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao executar consulta'
      )
    );
  }
};

// Test regex configuration for terminology mapping
export const testRegexConfiguration = async (regexConfig: RegexTestConfiguration): Promise<RegexTestResponse> => {
  try {
    const response = await axiosInstance.post('/data-sources/test-regex', regexConfig);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao testar configuração regex'
      )
    );
  }
};

// Associate agent with data source
export const addAgent = async (dataSourceId: number, agentId: number): Promise<DataSourceWithAgentsResponse> => {
  try {
    const response = await axiosInstance.post(
      `/data-sources/${dataSourceId}/agents/${agentId}`,
      {}
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao adicionar agente à fonte de dados'
      )
    );
  }
};

// Remove agent from data source
export const removeAgent = async (dataSourceId: number, agentId: number): Promise<DataSourceWithAgentsResponse> => {
  try {
    const response = await axiosInstance.delete(
      `/data-sources/${dataSourceId}/agents/${agentId}`
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao remover agente da fonte de dados'
      )
    );
  }
};
